import {
  MAX_COVER_TILL_AGE_PLUS,
  MAX_POLICY_TERM_YEARS,
  MIN_POLICY_TERM_YEARS,
  PLAN_NAMES,
} from "../../constants";

/**
 * Lead / quote-init APIs may return a single number or a one-element array.
 * @param {number|number[]|null|undefined} value
 * @returns {number|null}
 */
export function normalizeLeadNumericOption(value) {
  if (value == null) return null;
  const n = Number(Array.isArray(value) ? value[0] : value);
  return Number.isFinite(n) ? n : null;
}

/** Matches ProductEligibilityUtil.capPlusMaxCoverTillAge */
export function capPlusMaxCoverTillAge(leadMaxCoverTillAge) {
  const raw = normalizeLeadNumericOption(leadMaxCoverTillAge);
  if (raw == null) return null;
  return Math.min(raw, MAX_COVER_TILL_AGE_PLUS);
}

/** Matches ProductEligibilityUtil.capPlusMaxPolicyTerm */
export function capPlusMaxPolicyTerm(leadMaxPolicyTerm, attainedAgeYears) {
  const raw = normalizeLeadNumericOption(leadMaxPolicyTerm);
  if (raw == null) return null;
  if (attainedAgeYears == null || Number.isNaN(Number(attainedAgeYears))) {
    return raw;
  }
  const byCoverTillAgeCap = MAX_COVER_TILL_AGE_PLUS - Number(attainedAgeYears);
  if (byCoverTillAgeCap < MIN_POLICY_TERM_YEARS) {
    return null;
  }
  return Math.min(raw, byCoverTillAgeCap);
}

/**
 * Max cover-till age shown on quote (Plus capped at 75; Elite uses lead max).
 */
export function effectiveMaxCoverTillAgeForPlan(leadCoverTillOptions, plan) {
  const raw = normalizeLeadNumericOption(leadCoverTillOptions);
  if (raw == null) return null;
  return plan === PLAN_NAMES.PLUS ? capPlusMaxCoverTillAge(raw) : raw;
}

/**
 * Max policy term for stepped options / bulk grid (Plus capped by cover-till-age rule).
 * When Plus cannot satisfy min policy term to the 75 cover-till cap, returns null (do not fall back to uncapped lead PT).
 */
export function effectiveMaxPolicyTermForPlan(
  leadPolicyTermOptions,
  plan,
  attainedAgeYears,
) {
  const raw = normalizeLeadNumericOption(leadPolicyTermOptions);
  if (raw == null) return null;
  if (plan !== PLAN_NAMES.PLUS) return raw;
  return capPlusMaxPolicyTerm(raw, attainedAgeYears);
}

/**
 * When lead max policy term and max cover-till-age disagree (e.g. PT scalar 47 vs maturity 78),
 * use the higher implied PT (capped at MAX_POLICY_TERM_YEARS) so grid, desired input, and premium
 * stay aligned with sellable maturity. Plus still uses capped lead PT and cover-till cap.
 *
 * @param {{ leadPolicyTermOptions: *, leadCoverTillOptions: *, plan: string, attainedAgeYears: number|null|undefined }} p
 * @returns {number|null} null if no usable signal (caller may fall back)
 */
export function reconciledMaxPolicyTermForCoverTillGrid({
  leadPolicyTermOptions,
  leadCoverTillOptions,
  plan,
  attainedAgeYears,
}) {
  const rawPt = normalizeLeadNumericOption(leadPolicyTermOptions);
  const planMaxPt =
    plan === PLAN_NAMES.PLUS
      ? capPlusMaxPolicyTerm(rawPt, attainedAgeYears)
      : rawPt;
  const leadMaxPt =
    planMaxPt != null && Number.isFinite(Number(planMaxPt))
      ? Math.min(MAX_POLICY_TERM_YEARS, Number(planMaxPt))
      : null;

  const rawCt = normalizeLeadNumericOption(leadCoverTillOptions);
  const maxCoverTill =
    plan === PLAN_NAMES.PLUS
      ? capPlusMaxCoverTillAge(rawCt)
      : rawCt;

  let inferredMaxPt = null;
  if (
    maxCoverTill != null &&
    attainedAgeYears != null &&
    !Number.isNaN(Number(attainedAgeYears))
  ) {
    const mct = Number(maxCoverTill);
    const a = Number(attainedAgeYears);
    if (Number.isFinite(mct) && mct > a) {
      const d = mct - a;
      if (d >= MIN_POLICY_TERM_YEARS) {
        inferredMaxPt = Math.min(MAX_POLICY_TERM_YEARS, d);
      }
    }
  }

  if (leadMaxPt != null && inferredMaxPt != null) {
    return Math.min(MAX_POLICY_TERM_YEARS, Math.max(leadMaxPt, inferredMaxPt));
  }
  if (leadMaxPt != null) return leadMaxPt;
  if (inferredMaxPt != null) return inferredMaxPt;
  return null;
}

/**
 * Aligned cover-till-age / policy-term pairs for the quote grid.
 * Only includes rows where policy term is in [MIN_POLICY_TERM_YEARS, min(MAX_POLICY_TERM_YEARS, maxPolicyTermFromLead)].
 * Steps down by 5 from max cover till, up to 4 candidates (legacy UI).
 *
 * @param {{ maxCoverTillAge: number|null|undefined, attainedAgeYears: number|null|undefined, maxPolicyTermFromLead: number|null|undefined }} params
 * @returns {{ coverTillAge: number, policyTerm: number }[]}
 */
export function buildCoverTillPolicyTermPairs({
  maxCoverTillAge,
  attainedAgeYears,
  maxPolicyTermFromLead,
}) {
  const maxCover = Number(maxCoverTillAge);
  const age = Number(attainedAgeYears);
  if (!Number.isFinite(maxCover) || !Number.isFinite(age) || maxCover <= age) {
    return [];
  }
  const maxPtCeiling = Number.isFinite(Number(maxPolicyTermFromLead))
    ? Math.min(MAX_POLICY_TERM_YEARS, Number(maxPolicyTermFromLead))
    : MAX_POLICY_TERM_YEARS;
  const pairs = [];
  for (let i = 0; i < 4; i++) {
    const coverTillAge = maxCover - i * 5;
    if (coverTillAge <= age) break;
    const policyTerm = coverTillAge - age;
    if (
      policyTerm >= MIN_POLICY_TERM_YEARS &&
      policyTerm <= maxPtCeiling
    ) {
      pairs.push({ coverTillAge, policyTerm });
    }
  }
  pairs.sort((a, b) => a.coverTillAge - b.coverTillAge);
  return pairs;
}
